import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { purchaseOrdersAPI, vendorsAPI, warehouseAPI, crmJobsAPI, poDraftsAPI } from '../lib/api';
import { sendWithRecovery } from '../lib/poCreateRecovery';
import { perf } from '../lib/perf';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { formatQty, coerceQty } from '../lib/qtyUtils';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
  Plus, Loader2, Edit2, Trash2, Download, ShoppingCart, 
  Building2, Briefcase, FileText, DollarSign, X, ClipboardList, Filter, Search, Shield, AlertTriangle, AlertCircle, MapPin, Warehouse, Printer, Mail, Tag, Package, Clock, CheckCircle2, ArrowRight, Zap, Link2, XCircle, FileEdit, ChevronDown, PackageCheck, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ItemSearchCombobox } from '../components/ItemSearchCombobox';
import { PrintablePO } from '../components/po/PrintablePO';
import { EmailVendorModal } from '../components/po/EmailVendorModal';
import { NotificationHistoryRow } from '../components/po/NotificationHistoryRow';
import { POEmailIssuesIndicator } from '../components/po/POEmailIssuesIndicator';
import { PartialReceiveModal } from '../components/PartialReceiveModal';
import { POAssignmentModal } from '../components/POAssignmentModal';
import { formatInWarehouseTimezone, formatRelativeWithTimezone } from '../lib/timezone';
import { useSortableTable } from '../hooks/useSortableTable';
import { SortableTableHead } from '../components/ui/SortableTableHead';

const PO_SORT_COLUMNS = [
  { key: 'po_number', type: 'text' },
  { key: 'total_items', type: 'number' },
  { key: 'total', type: 'currency' },
  { key: 'payment_due_date', type: 'date' },
  { key: 'status', type: 'status' },
];
import NewPODrawer from '../components/purchase_orders/NewPODrawer';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import { POActivityModal } from '../components/POActivityModal';
import { KebabMenu } from '../components/KebabMenu';
import { PODocumentsPanel } from '../components/po/PODocumentsPanel';
import { POPagination, PO_PAGE_SIZES } from '../components/po/POPagination';
import { useLiveRefresh } from '../hooks/useLiveRefresh';

const NO_ROWS = [];

// Helper function to show toast based on PO fully received notification outcome
const showFullyReceivedNotificationToast = (notification) => {
  if (!notification || !notification.attempted) {
    // No notification was attempted, don't show any toast for this
    return;
  }
  
  if (notification.sent) {
    // Email was sent successfully
    toast.success('PO marked as Fully Received. Notification email sent.');
  } else {
    // Email was not sent - show reason
    switch (notification.reason) {
      case 'missing_job_number':
        toast.info('PO marked as Fully Received. No notification sent (no job number).');
        break;
      case 'missing_to_address':
        toast.info('PO marked as Fully Received. No notification sent (no recipient email configured).');
        break;
      case 'email_send_failed':
        toast.warning('PO marked as Fully Received, but the notification email failed to send.');
        break;
      default:
        // For other reasons like 'already_sent', show nothing
        break;
    }
  }
};

const STATUS_COLORS = {
  'Pending': 'bg-yellow-100 text-yellow-800',
  'Rejected': 'bg-red-100 text-red-800',
  'Needs Picked Up': 'bg-orange-100 text-orange-800',
  'Partially Received': 'bg-blue-100 text-blue-800',
  'Pending Assignment': 'bg-amber-100 text-amber-800',  // PHASE 3A: New status
  'Received': 'bg-green-100 text-green-800',
  'Awaiting Warranty Part': 'bg-purple-100 text-purple-800',
};

// Base statuses for all POs
const BASE_STATUS_OPTIONS = ['Pending', 'Rejected', 'Needs Picked Up', 'Partially Received', 'Pending Assignment', 'Received'];
// Additional status for warranty POs only
const WARRANTY_STATUS_OPTIONS = [...BASE_STATUS_OPTIONS, 'Awaiting Warranty Part'];
const DATE_RANGE_OPTIONS = [
  { label: 'All Time', value: 'all' },
  { label: 'Last 7 Days', value: '7' },
  { label: 'Last 30 Days', value: '30' },
  { label: 'Last 90 Days', value: '90' },
];

export const PurchaseOrders = () => {
  const { user, isOwner, isOffice, hasPerm, hasEntitlement } = useAuth();
  const hasCrm = hasEntitlement('crm');
  const [searchParams, setSearchParams] = useSearchParams();
  const canSeeCosts = hasPerm('po.approve');  // Permission-based cost visibility
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);  // Track fetch errors for retry UI
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [departments, setDepartments] = useState([]);  // Phase 4: PO Departments
  
  // New PO Drawer state (replaces tabs)
  const [newPODrawerOpen, setNewPODrawerOpen] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [prefillProcessed, setPrefillProcessed] = useState(false);  // Phase 5A: Track if prefill was processed
  
  // Server-side drafts state
  const [drafts, setDrafts] = useState([]);
  const [draftsExpanded, setDraftsExpanded] = useState(true);
  const [editingDraft, setEditingDraft] = useState(null); // PODraftResponse when editing
  const [discardingDraftId, setDiscardingDraftId] = useState(null);
  const [submittingDraftId, setSubmittingDraftId] = useState(null);
  
  // Filter state (Phase 6: initialised from, and kept in, the page address)
  const [statusFilter, setStatusFilterRaw] = useState(() => searchParams.get('status') || 'all');
  const [vendorFilter, setVendorFilterRaw] = useState(() => searchParams.get('vendor') || 'all');
  const [warehouseFilter, setWarehouseFilterRaw] = useState(() => searchParams.get('warehouseId') || 'all');
  const [departmentFilter, setDepartmentFilterRaw] = useState(() => searchParams.get('dept') || 'all');  // Phase 4: Department filter
  const [dateRangeFilter, setDateRangeFilterRaw] = useState(() => searchParams.get('range') || 'all');
  const [paymentTermsFilter, setPaymentTermsFilterRaw] = useState(() => searchParams.get('terms') || 'all');  // Stage 5: Payment terms filter
  const [dueDateFilter, setDueDateFilterRaw] = useState(() => searchParams.get('due') || 'all');  // Due date urgency filter
  const [searchFilter, setSearchFilterRaw] = useState(() => searchParams.get('q') || '');  // Search by tech name, PO #, or job number
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('q') || '');
  const [page, setPage] = useState(() => Math.max(1, parseInt(searchParams.get('page'), 10) || 1));
  const [pageSize, setPageSizeRaw] = useState(() => (PO_PAGE_SIZES.includes(Number(searchParams.get('size'))) ? Number(searchParams.get('size')) : 25));
  const [poPage, setPoPage] = useState({ total: 0, total_all: 0, pages: 1 });
  const [poBusy, setPoBusy] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const poRequestSeq = useRef(0);
  const lastGoodPage = useRef(1);
  // Any filter change returns to page 1 (same render, so only one request is made)
  const resetsPage = (setter) => (value) => { setter(value); setPage(1); };
  const setStatusFilter = resetsPage(setStatusFilterRaw);
  const setVendorFilter = resetsPage(setVendorFilterRaw);
  const setWarehouseFilter = resetsPage(setWarehouseFilterRaw);
  const setDepartmentFilter = resetsPage(setDepartmentFilterRaw);
  const setDateRangeFilter = resetsPage(setDateRangeFilterRaw);
  const setPaymentTermsFilter = resetsPage(setPaymentTermsFilterRaw);
  const setDueDateFilter = resetsPage(setDueDateFilterRaw);
  const setPageSize = resetsPage(setPageSizeRaw);
  const setSearchFilter = setSearchFilterRaw;
  // Sorting is done by the server; the hook only tracks the chosen column (kept in the URL)
  const { sortKey: poSortKey, sortDir: poSortDir, onSort: poOnSortRaw } = useSortableTable(NO_ROWS, { columns: PO_SORT_COLUMNS, persistUrl: true });
  const poOnSort = (key) => { poOnSortRaw(key); setPage(1); };
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);  // Mobile filters dropdown
  const [fabOpen, setFabOpen] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  
  // Edit form job search (CRM mode)
  const [editJobSearchQuery, setEditJobSearchQuery] = useState('');
  const [editJobSearchResults, setEditJobSearchResults] = useState([]);
  const [editJobSearching, setEditJobSearching] = useState(false);
  const [editSelectedJob, setEditSelectedJob] = useState(null);
  const [editJobSearchOpen, setEditJobSearchOpen] = useState(false);
  
  // Print modal state
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printingPO, setPrintingPO] = useState(null);
  
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailingPO, setEmailingPO] = useState(null);
  
  // Warranty credit prompt
  const [warrantyCreditPromptOpen, setWarrantyCreditPromptOpen] = useState(false);
  const [pendingWarrantyUpdate, setPendingWarrantyUpdate] = useState(null);
  
  // Inline vendor add (within PO drawer)
  const [showInlineVendorAdd, setShowInlineVendorAdd] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [savingVendor, setSavingVendor] = useState(false);
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingPO, setDeletingPO] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // P2: Receiving state
  const [receivingMode, setReceivingMode] = useState(false);
  const [receivingQuantities, setReceivingQuantities] = useState({});
  const [receivingSaving, setReceivingSaving] = useState(false);
  
  // Partial Receive Modal state
  const [partialReceiveModalOpen, setPartialReceiveModalOpen] = useState(false);
  const [partialReceivePO, setPartialReceivePO] = useState(null);
  const [quickReceiveOpen, setQuickReceiveOpen] = useState(false);
  const [quickReceivePO, setQuickReceivePO] = useState(null);
  const [activityPO, setActivityPO] = useState(null);
  const [documentsPO, setDocumentsPO] = useState(null);
  const [quickReceiveConfirmOpen, setQuickReceiveConfirmOpen] = useState(false);
  const [quickReceiveBusy, setQuickReceiveBusy] = useState(false);

  // PHASE 3: PO Assignment Modal state (triggered when PO is fully received)
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentPO, setAssignmentPO] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState('assign');
  const canReceiveAndAssign = isOwner || isOffice;
  const openReceiveAndAssign = (po) => {
    setAssignmentMode('receive');
    setAssignmentPO(po);
    setAssignmentModalOpen(true);
  };
  const closeAssignment = (refresh = assignmentMode === 'receive') => {
    if (refresh) fetchData();
    setAssignmentModalOpen(false);
    setAssignmentPO(null);
    setAssignmentMode('assign');
  };

  const fetchData = async () => {
    await Promise.all([fetchAux(), fetchPOs({ fresh: true })]);
  };

  // Vendors, warehouses, departments and drafts (everything except the PO page itself)
  const fetchAux = async ({ fresh = false } = {}) => {
    try {
      const [vendorsRes, warehousesRes, departmentsRes, draftsRes] = await Promise.all([
        vendorsAPI.list(),
        warehouseAPI.listWarehouses(),
        purchaseOrdersAPI.listDepartments(),  // Phase 4: Fetch departments
        poDraftsAPI.list().catch(() => ({ data: [] })),  // Drafts — graceful fallback
      ]);
      setVendors(vendorsRes.data);
      setWarehouses(warehousesRes.data);
      setDepartments(departmentsRes.data);  // Phase 4
      setDrafts(draftsRes.data || []);
    } catch (error) {
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED' && !fresh) {
        toast.error('Failed to load vendors and warehouses');
      }
    }
  };

  const poParams = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (statusFilter !== 'all') p.status = statusFilter;
    if (vendorFilter !== 'all') p.vendor_id = vendorFilter;
    if (warehouseFilter !== 'all') p.warehouse_id = warehouseFilter;
    if (departmentFilter !== 'all') p.department_id = departmentFilter;
    if (dateRangeFilter !== 'all') p.submitted_after = subDays(new Date(), parseInt(dateRangeFilter, 10)).toISOString();
    if (paymentTermsFilter !== 'all') p.payment_terms = paymentTermsFilter;
    if (dueDateFilter !== 'all') { p.due = dueDateFilter; p.today = format(new Date(), 'yyyy-MM-dd'); }
    if (poSortKey) { p.sort_by = poSortKey; p.sort_dir = poSortDir || 'asc'; }
    return p;
  }, [page, pageSize, debouncedSearch, statusFilter, vendorFilter, warehouseFilter, departmentFilter, dateRangeFilter, paymentTermsFilter, dueDateFilter, poSortKey, poSortDir]);

  // Phase 6: load only the current page from the server. Stale responses are ignored; on a failed
  // background refresh the current rows stay on screen.
  const fetchPOs = useCallback(async ({ silent = false, fresh = false } = {}) => {
    const seq = ++poRequestSeq.current;
    if (!silent) setPoBusy(true);
    perf.markStart('po-page');
    try {
      const { data } = await purchaseOrdersAPI.listPaged(poParams, { fresh });
      if (seq !== poRequestSeq.current) return;
      setPurchaseOrders(data.items);
      setPoPage({ total: data.total, total_all: data.total_all, pages: data.pages });
      if (data.page !== poParams.page) setPage(data.page);
      lastGoodPage.current = data.page;
      setLoadError(null);
      setRefreshFailed(false);
    } catch (error) {
      if (seq !== poRequestSeq.current || error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
      if (silent) {
        setRefreshFailed(true);
      } else {
        // Keep the page indicator in step with the rows still on screen
        if (poParams.page !== lastGoodPage.current) setPage(lastGoodPage.current);
        setRefreshFailed(false);
        setLoadError('Failed to load purchase orders');
        toast.error('Failed to load purchase orders');
      }
    } finally {
      if (seq === poRequestSeq.current) {
        setPoBusy(false);
        setLoading(false);
        perf.markEnd('po-page');
      }
    }
  }, [poParams]);

  useEffect(() => { fetchPOs(); }, [fetchPOs]);

  useEffect(() => {
    fetchAux();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search; the page reset happens in the same update, so only one request is made
  useEffect(() => {
    if (searchFilter === debouncedSearch) return undefined;
    const t = setTimeout(() => { setDebouncedSearch(searchFilter); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep page, size and filters in the page address (replace, so Back still leaves the page)
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    const put = (k, v, def) => { if (v === def || v === '' || v == null) next.delete(k); else next.set(k, String(v)); };
    put('page', page, 1); put('size', pageSize, 25); put('q', debouncedSearch.trim(), '');
    put('status', statusFilter, 'all'); put('vendor', vendorFilter, 'all'); put('dept', departmentFilter, 'all');
    put('range', dateRangeFilter, 'all'); put('terms', paymentTermsFilter, 'all'); put('due', dueDateFilter, 'all');
    if (next.toString() !== new URLSearchParams(window.location.search).toString()) setSearchParams(next, { replace: true });
  }, [page, pageSize, debouncedSearch, statusFilter, vendorFilter, departmentFilter, dateRangeFilter, paymentTermsFilter, dueDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 6: revalidate after any write, on tab focus, and every 60s while visible.
  // Only the list refreshes; open forms and receiving windows keep their own state.
  useLiveRefresh(({ reason }) => {
    fetchPOs({ silent: true, fresh: true });
    setEmailIssuesKey((k) => k + 1);
    if (reason !== 'interval') fetchAux({ fresh: true });
  });

  // Handle URL params for deep-linking (backward compatibility)
  // ?tab=new or ?new=1 => open drawer
  // ?tab=all or no param => drawer closed
  useEffect(() => {
    if (loading) return;
    
    const tabParam = searchParams.get('tab');
    const newParam = searchParams.get('new');
    
    // Open drawer if tab=new or new=1
    if (tabParam === 'new' || newParam === '1') {
      setNewPODrawerOpen(true);
    }
  }, [loading, searchParams]);

  // Sync URL when drawer opens/closes
  const handleDrawerOpenChange = (open) => {
    setNewPODrawerOpen(open);
    
    if (open) {
      // Add ?tab=new to URL without adding history entry
      searchParams.set('tab', 'new');
      setSearchParams(searchParams, { replace: true });
    } else {
      // Remove tab param when closing
      searchParams.delete('tab');
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
      // Clear editing draft when drawer closes
      setEditingDraft(null);
    }
  };

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      const newParam = params.get('new');
      setNewPODrawerOpen(tabParam === 'new' || newParam === '1');
      // Sync warehouse filter from URL on popstate
      setWarehouseFilterRaw(params.get('warehouseId') || 'all');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Persist warehouseFilter in URL
  useEffect(() => {
    const current = searchParams.get('warehouseId');
    const target = warehouseFilter === 'all' ? null : warehouseFilter;
    if (current !== target) {
      if (target) {
        searchParams.set('warehouseId', target);
      } else {
        searchParams.delete('warehouseId');
      }
      setSearchParams(searchParams, { replace: true });
    }
  }, [warehouseFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link: Handle ?po={id} parameter to open a specific PO
  useEffect(() => {
    if (loading) return;
    
    const poId = searchParams.get('po');
    if (poId) {
      // Phase 6: the PO may not be on the current page, so fall back to loading it directly
      const targetPO = purchaseOrders.find(po => po.id === poId);
      searchParams.delete('po');
      setSearchParams(searchParams, { replace: true });
      if (targetPO) {
        openEditModal(targetPO);
      } else {
        purchaseOrdersAPI.get(poId).then((res) => openEditModal(res.data)).catch(() => toast.error('Purchase order not found'));
      }
    }
  }, [loading, purchaseOrders, searchParams, setSearchParams]);

  // Phase 5A: Handle prefill from Part Request conversion
  useEffect(() => {
    if (loading || prefillProcessed) return;
    
    const shouldPrefill = searchParams.get('prefill') === 'true';
    if (shouldPrefill) {
      const prefillDataStr = sessionStorage.getItem('prefill_po');
      if (prefillDataStr) {
        try {
          const data = JSON.parse(prefillDataStr);
          setPrefillData(data);
          setNewPODrawerOpen(true);
        } catch (e) {
          console.error('Failed to parse prefill data:', e);
        }
        sessionStorage.removeItem('prefill_po');
      }
      // Clear the URL param
      searchParams.delete('prefill');
      setSearchParams(searchParams, { replace: true });
      setPrefillProcessed(true);
    }
  }, [loading, prefillProcessed, searchParams, setSearchParams]);

  // Handle PO created from drawer
  const handlePOCreated = (newPO) => {
    fetchData(); // Refresh list
    // Close drawer and clean URL (handled by handleDrawerOpenChange)
  };

  // --- Draft action handlers ---
  const handleEditDraft = (draft) => {
    setEditingDraft(draft);
    setNewPODrawerOpen(true);
  };

  const handleDraftSaved = () => {
    // Refresh drafts list
    poDraftsAPI.list().then(res => setDrafts(res.data || [])).catch(() => {});
  };

  const handleDraftSubmitted = () => {
    setEditingDraft(null);
    fetchData(); // Refresh both POs and drafts
  };

  const handleDiscardDraftFromList = async (draftId) => {
    setDiscardingDraftId(draftId);
    try {
      await poDraftsAPI.delete(draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Draft discarded');
    } catch {
      toast.error('Failed to discard draft');
    } finally {
      setDiscardingDraftId(null);
    }
  };

  const handleSubmitDraftFromList = async (draftId) => {
    setSubmittingDraftId(draftId);
    try {
      const res = await sendWithRecovery(() => poDraftsAPI.submit(draftId));
      const newPO = res.data;
      toast.success(`PO ${newPO.po_number} created from draft`);
      fetchData(); // Refresh POs + drafts
    } catch (error) {
      if (error.uncertain) {
        toast.warning("We couldn't confirm whether the draft was submitted. Tap Submit again — it will never create a second PO.", { duration: 10000 });
        return;
      }
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : (detail?.message || detail || 'Failed to submit draft');
      toast.error(msg);
    } finally {
      setSubmittingDraftId(null);
    }
  };

  const handleDuplicateDraft = async (draftId) => {
    try {
      await poDraftsAPI.duplicate(draftId);
      poDraftsAPI.list().then(res => setDrafts(res.data || [])).catch(() => {});
      toast.success('Draft duplicated');
    } catch {
      toast.error('Failed to duplicate draft');
    }
  };


  const clearFilters = () => {
    setStatusFilter('all');
    setVendorFilter('all');
    setWarehouseFilter('all');
    setDepartmentFilter('all');  // Phase 4
    setDateRangeFilter('all');
    setPaymentTermsFilter('all');  // Stage 5
    setDueDateFilter('all');
    setSearchFilter('');
  };

  // Clear filters except search (for mobile dropdown)
  const clearFiltersExceptSearch = () => {
    setStatusFilter('all');
    setVendorFilter('all');
    setWarehouseFilter('all');
    setDepartmentFilter('all');  // Phase 4
    setDateRangeFilter('all');
    setPaymentTermsFilter('all');  // Stage 5
    setDueDateFilter('all');
  };

  const hasActiveFilters = statusFilter !== 'all' || vendorFilter !== 'all' || warehouseFilter !== 'all' || departmentFilter !== 'all' || dateRangeFilter !== 'all' || paymentTermsFilter !== 'all' || dueDateFilter !== 'all' || searchFilter.trim() !== '';
  
  // Resolve selected warehouse name (null if "all" or warehouse not found)
  const selectedWarehouseName = warehouseFilter !== 'all'
    ? warehouses.find(w => w.id === warehouseFilter)?.name || null
    : null;

  // Graceful fallback: if warehouseId in URL doesn't match any loaded warehouse, reset
  useEffect(() => {
    if (!loading && warehouseFilter !== 'all' && warehouses.length > 0 && !selectedWarehouseName) {
      setWarehouseFilterRaw('all'); setPage(1);
    }
  }, [loading, warehouses, warehouseFilter, selectedWarehouseName]);
  
  // Count active filters (excluding search for badge)
  const activeFilterCount = [
    statusFilter !== 'all',
    vendorFilter !== 'all',
    warehouseFilter !== 'all',
    departmentFilter !== 'all',  // Phase 4
    dateRangeFilter !== 'all',
    paymentTermsFilter !== 'all',  // Stage 5
    dueDateFilter !== 'all',
  ].filter(Boolean).length;

  const openEditModal = async (po) => {
    // If slim mode stripped line_items, fetch full detail
    let fullPO = po;
    if (!po.line_items || po.line_items.length === 0) {
      try {
        const res = await purchaseOrdersAPI.get(po.id);
        fullPO = res.data;
      } catch {
        toast.error('Failed to load PO details');
        return;
      }
    }
    setEditingPO(fullPO);
    setEditFormData({
      vendor_id: fullPO.vendor_id,
      department_id: fullPO.department_id || '',
      job_id: fullPO.job_id || null,
      job_name: fullPO.job_name || '',
      job_number: fullPO.job_number || '',
      order_description: fullPO.order_description || '',
      line_items: fullPO.line_items.map(li => ({
        id: li.id,
        item_id: li.item_id || null,
        item_name: li.item_name,
        quantity: coerceQty(li.quantity) || 1,
        unit_cost: li.unit_cost?.toString() || '',
        auto_created_item: li.auto_created_item || false,
      })),
      office_cost: fullPO.office_cost?.toString() || '',
      office_notes: fullPO.office_notes || '',
      expected_billable_value: fullPO.expected_billable_value?.toString() || '',
      status: fullPO.status,
    });
    setShowInlineVendorAdd(false);
    setNewVendorName('');
    // Initialize job search state for edit
    if (fullPO.job_id) {
      setEditSelectedJob({ id: fullPO.job_id, job_number: fullPO.job_number, job_name: fullPO.job_name });
    } else {
      setEditSelectedJob(null);
    }
    setEditJobSearchQuery('');
    setEditJobSearchResults([]);
    setEditJobSearchOpen(false);
    setEditModalOpen(true);
  };

  // CRM job search for edit form
  useEffect(() => {
    if (!hasCrm || !editJobSearchQuery || editJobSearchQuery.length < 2) { setEditJobSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setEditJobSearching(true);
      try {
        const res = await crmJobsAPI.list({ search: editJobSearchQuery, limit: 10 });
        setEditJobSearchResults(res.data.jobs || []);
      } catch { setEditJobSearchResults([]); }
      finally { setEditJobSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [editJobSearchQuery, hasCrm]);


  /**
   * Manually resend the "PO Fully Received" email when the original send
   * silently failed (status='Received' but fully_received_email_sent_at is
   * still null). Owner/office only.
   */
  const handleResendFullyReceivedEmail = async (confirmDuplicate = false) => {
    if (!editingPO?.id || resendingEmail) return;
    setResendingEmail(true);
    try {
      const res = await purchaseOrdersAPI.resendFullyReceivedEmail(editingPO.id, confirmDuplicate === true);
      if (res.data?.ok) {
        toast.success('PO Fully Received email sent');
        // Refresh the PO detail so the banner disappears once
        // fully_received_email_sent_at is populated.
        try {
          const fresh = await purchaseOrdersAPI.get(editingPO.id);
          setEditingPO(fresh.data);
        } catch (_) {
          // Soft-fail: list will refetch on next interaction.
        }
        await fetchData();
      } else {
        const outcome = res.data?.fully_received_notification || {};
        if (outcome.reason === 'delivery_uncertain') {
          setResendingEmail(false);
          if (window.confirm(`Delivery uncertain.\n\n${outcome.guidance}\n\nSend it again anyway?`)) {
            return handleResendFullyReceivedEmail(true);
          }
          return;
        }
        toast.error(`Could not send email (reason: ${outcome.reason || 'unknown'})`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Network error';
      toast.error(`Failed to resend: ${detail}`);
    } finally {
      setResendingEmail(false);
    }
  };

  // Dry-run inspector for the PO Fully Received notification. Surfaces the
  // resolved To/CC, the underlying recipient layers, and the precise reason
  // a live send would skip — without sending an email. Helpful when the
  // resend toast says "failed" and the user/support need to know why.
  const [emailDiagnostics, setEmailDiagnostics] = useState(null);
  const [emailIssuesKey, setEmailIssuesKey] = useState(0);
  const [retryingNotificationId, setRetryingNotificationId] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const handleShowEmailDiagnostics = async () => {
    if (!editingPO?.id || loadingDiagnostics) return;
    setLoadingDiagnostics(true);
    try {
      const res = await purchaseOrdersAPI.getEmailDiagnostics(editingPO.id);
      setEmailDiagnostics(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Network error';
      toast.error(`Could not load diagnostics: ${detail}`);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const handleRetryNotification = async (entry) => {
    const poId = emailDiagnostics?.po_id || editingPO?.id;
    if (retryingNotificationId) return;
    let confirmDuplicate = false;
    if (entry.requires_duplicate_confirmation) {
      if (!window.confirm('Delivery uncertain: the mail server may already have accepted this email. Check with the recipients first.\n\nSend it again anyway?')) return;
      confirmDuplicate = true;
    }
    setRetryingNotificationId(entry.id);
    try {
      const res = await purchaseOrdersAPI.retryNotification(poId, entry.id, confirmDuplicate);
      const n = res.data?.notification || {};
      if (res.data?.ok) toast.success('Notification sent');
      else toast.error(`Not sent (${n.status}): ${n.reason || 'unknown'}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Retry failed'));
    }
    try {
      const res = await purchaseOrdersAPI.getEmailDiagnostics(poId);
      setEmailDiagnostics(res.data);
    } catch {
      toast.error('Could not refresh notification history');
    } finally {
      setRetryingNotificationId(null);
      setEmailIssuesKey((k) => k + 1);
    }
  };
  const openEmailDiagnosticsFor = async (poId) => {
    try {
      const res = await purchaseOrdersAPI.getEmailDiagnostics(poId);
      setEmailDiagnostics(res.data);
    } catch (err) {
      toast.error(`Could not load notification history: ${err.response?.data?.detail || err.message}`);
    }
  };
  const handleEditSubmit = async (e, warrantyCreditValue = null) => {
    if (e) e.preventDefault();
    
    const validLineItems = editFormData.line_items.filter(li => li.item_name.trim());
    if (validLineItems.length === 0) {
      toast.error('At least one line item is required');
      return;
    }
    
    // Check if this is a warranty PO being marked as Received - need to prompt for credit
    const isWarrantyPO = editingPO?.is_warranty;
    const isChangingToReceived = canSeeCosts && editFormData.status === 'Received' && editingPO?.status !== 'Received';
    
    if (isWarrantyPO && isChangingToReceived && warrantyCreditValue === null) {
      // Store pending update and show warranty credit prompt
      setPendingWarrantyUpdate({ validLineItems });
      setWarrantyCreditPromptOpen(true);
      return;
    }
    
    setEditSaving(true);
    try {
      const payload = {
        vendor_id: editFormData.vendor_id,
        department_id: editFormData.department_id === '__none__' ? null : (editFormData.department_id || null),
        job_id: editFormData.job_id || null,
        job_name: editFormData.job_name,
        job_number: editFormData.job_number,
        order_description: editFormData.order_description || null,
        line_items: validLineItems.map(li => ({
          item_id: li.item_id || null,  // Link to Parts List (null for one-offs)
          item_name: li.item_name.trim(),
          quantity: parseInt(li.quantity) || 1,
          unit_cost: li.unit_cost ? parseFloat(li.unit_cost) : null,
        })),
      };
      
      // Add office fields for owners
      if (canSeeCosts) {
        payload.office_cost = editFormData.office_cost ? parseFloat(editFormData.office_cost) : null;
        payload.office_notes = editFormData.office_notes || null;
        payload.expected_billable_value = editFormData.expected_billable_value ? parseFloat(editFormData.expected_billable_value) : null;
        payload.status = editFormData.status;
        
        // Add warranty credit if provided (for warranty POs being marked as Received)
        if (warrantyCreditValue !== null) {
          payload.warranty_credit_received = warrantyCreditValue;
        }
      }
      
      // Track if we're changing to Received to show notification toast
      const isChangingToReceivedNow = canSeeCosts && editFormData.status === 'Received' && editingPO?.status !== 'Received';
      
      const response = await purchaseOrdersAPI.update(editingPO.id, payload);
      const updatedPO = response.data;
      
      // Check for fully_received_notification in response
      if (isChangingToReceivedNow && updatedPO.fully_received_notification) {
        showFullyReceivedNotificationToast(updatedPO.fully_received_notification);
      } else {
        let successMsg = 'Purchase Order updated';
        if (warrantyCreditValue !== null) {
          successMsg = warrantyCreditValue 
            ? 'Warranty part received — credit confirmed'
            : 'Warranty part received — credit pending';
        }
        toast.success(successMsg);
      }
      
      setEditModalOpen(false);
      setWarrantyCreditPromptOpen(false);
      setPendingWarrantyUpdate(null);
      fetchData();
      
      // PHASE 3A: If backend says there's pending assignment, trigger assignment modal
      if (updatedPO.has_pending_assignment) {
        setAssignmentPO(updatedPO);
        setAssignmentModalOpen(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update PO';
      // Provide user-friendly messages for common errors
      if (errorMsg.includes('only edit') && errorMsg.includes('Pending')) {
        toast.error('This PO can no longer be edited because its status has changed from Pending.');
      } else if (errorMsg.includes('only edit your own')) {
        toast.error('You can only edit your own Purchase Orders.');
      } else if (errorMsg.includes('warranty_credit_received is required')) {
        toast.error('Please confirm whether warranty credit was received.');
      } else if (errorMsg.includes('Awaiting Warranty Part')) {
        toast.error("'Awaiting Warranty Part' status is only valid for warranty POs.");
      } else if (errorMsg.includes('Assignment required')) {
        // PHASE 3A: Handle 409 for missing assignment
        toast.warning('Cannot mark as Received. Please assign inventory first.');
        // Open assignment modal
        if (editingPO) {
          setAssignmentPO(editingPO);
          setAssignmentModalOpen(true);
        }
      } else if (errorMsg.includes('Cannot transition') || errorMsg.includes('Cannot set')) {
        // Lifecycle transition guard — show a friendly summary
        if (errorMsg.includes('Pending Assignment') && errorMsg.includes('total received')) {
          toast.error("Can't set this status yet — items haven't been fully received.");
        } else if (errorMsg.includes('Partially Received') && errorMsg.includes('no items')) {
          toast.error("Can't set Partially Received — no items have been received yet.");
        } else if (errorMsg.includes('inventory has not been assigned')) {
          toast.warning("Can't mark as Received — inventory hasn't been assigned yet.");
          if (editingPO) {
            setAssignmentPO(editingPO);
            setAssignmentModalOpen(true);
          }
        } else if (errorMsg.includes('no ordered items')) {
          toast.error("Can't change status — this PO has no ordered items.");
        } else if (errorMsg.includes('terminal') || errorMsg.includes('none (terminal)')) {
          toast.error("This PO's status is final and can't be changed.");
        } else {
          toast.error("Status change not allowed from the current state.");
        }
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setEditSaving(false);
    }
  };
  
  // Handle warranty credit prompt response
  const handleWarrantyCreditResponse = (creditReceived) => {
    setWarrantyCreditPromptOpen(false);
    handleEditSubmit(null, creditReceived);
  };

  const addEditLineItem = () => {
    setEditFormData({
      ...editFormData,
      line_items: [...editFormData.line_items, { item_id: null, item_name: '', quantity: 1, unit_cost: '' }],
    });
  };

  const updateEditLineItem = (index, field, value) => {
    const newItems = [...editFormData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditFormData({ ...editFormData, line_items: newItems });
  };

  // Handle item selection from search combobox in edit modal
  const handleEditItemSelect = (index, selection) => {
    const newItems = [...editFormData.line_items];
    newItems[index] = {
      ...newItems[index],
      item_id: selection.item_id,
      item_name: selection.item_name,
      // Auto-fill unit_cost if item has one and current cost is empty
      unit_cost: selection.unit_cost && !newItems[index].unit_cost ? selection.unit_cost : newItems[index].unit_cost
    };
    setEditFormData({ ...editFormData, line_items: newItems });
  };

  const removeEditLineItem = (index) => {
    if (editFormData.line_items.length > 1) {
      setEditFormData({
        ...editFormData,
        line_items: editFormData.line_items.filter((_, i) => i !== index),
      });
    }
  };

  // P2: Receiving functions
  const initializeReceiving = () => {
    if (!editingPO) return;
    const quantities = {};
    editingPO.line_items.forEach(li => {
      quantities[li.id] = coerceQty(li.quantity_received);
    });
    setReceivingQuantities(quantities);
    setReceivingMode(true);
  };

  // Open receive modal with full PO data (slim list omits line_items)
  const openReceiveModal = async (po) => {
    let fullPO = po;
    if (!po.line_items || po.line_items.length === 0) {
      try {
        const res = await purchaseOrdersAPI.get(po.id);
        fullPO = res.data;
      } catch {
        toast.error('Failed to load PO details');
        return;
      }
    }
    setPartialReceivePO(fullPO);
    setPartialReceiveModalOpen(true);
  };

  // ⚡ Receive all & assign — smart flow
  const openQuickReceive = async (po) => {
    if (quickReceiveBusy) return;
    let fullPO = po;
    if (!po.line_items || po.line_items.length === 0) {
      try {
        const res = await purchaseOrdersAPI.get(po.id);
        fullPO = res.data;
      } catch {
        toast.error('Failed to load PO details');
        return;
      }
    }
    const totalRemaining = (fullPO.line_items || []).reduce((sum, li) => {
      return sum + Math.max(0, Number(li.quantity) - Number(li.quantity_received || 0));
    }, 0);
    if (totalRemaining <= 0) {
      toast.info('All items on this PO are already received.');
      return;
    }
    setQuickReceivePO(fullPO);
    // Confirmation needed if remaining > 10 OR already partially received
    const needsConfirm = totalRemaining > 10 || fullPO.status === 'Partially Received';
    if (needsConfirm) {
      setQuickReceiveConfirmOpen(true);
    } else {
      await executeReceiveAll(fullPO);
    }
  };

  const executeReceiveAll = async (po) => {
    if (quickReceiveBusy) return;
    setQuickReceiveBusy(true);
    setQuickReceiveConfirmOpen(false);
    try {
      const itemsWithRemaining = (po.line_items || []).filter(li =>
        Number(li.quantity) - Number(li.quantity_received || 0) > 0
      );
      const items = itemsWithRemaining.map(li => ({
        line_item_id: li.id,
        quantity_received: Number(li.quantity),
      }));
      const response = await purchaseOrdersAPI.receiveItems(po.id, items);
      const updatedPO = response.data.purchase_order;
      // Update list
      handlePartialReceiveSuccess(updatedPO);
      // Chain to Assign drawer if assignment pending
      if (updatedPO.has_pending_assignment) {
        toast.success('All items received. Opening assignment...');
        setAssignmentPO(updatedPO);
        setAssignmentModalOpen(true);
      } else {
        toast.success('PO fully received');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to receive items';
      toast.error(typeof msg === 'string' ? msg : 'Receive failed');
    } finally {
      setQuickReceiveBusy(false);
      setQuickReceivePO(null);
    }
  };

  // Handle status change - intercept "Partially Received" to open modal
  const handleStatusChange = (newStatus) => {
    if (newStatus === 'Partially Received' && editingPO) {
      // Open the partial receive modal instead of just changing status
      setPartialReceivePO(editingPO);
      setPartialReceiveModalOpen(true);
    } else {
      // For other statuses, update normally
      setEditFormData({ ...editFormData, status: newStatus });
    }
  };

  // Handle partial receive modal success
  const handlePartialReceiveSuccess = (updatedPO) => {
    // Update the PO in the list - use functional update to avoid stale closure
    setPurchaseOrders(prevPOs => prevPOs.map(po => 
      po.id === updatedPO.id ? updatedPO : po
    ));
    // Update the editing PO
    setEditingPO(updatedPO);
    // Update the form data status to reflect the new status
    if (editFormData) {
      setEditFormData({ ...editFormData, status: updatedPO.status });
    }
  };

  const updateReceivingQuantity = (lineItemId, value) => {
    setReceivingQuantities(prev => ({
      ...prev,
      [lineItemId]: Math.max(0, parseInt(value) || 0)
    }));
  };

  const handleReceiveAll = () => {
    if (!editingPO) return;
    const allReceived = {};
    editingPO.line_items.forEach(li => {
      allReceived[li.id] = coerceQty(li.quantity);
    });
    setReceivingQuantities(allReceived);
  };

  const handleSaveReceiving = async () => {
    if (!editingPO) return;
    setReceivingSaving(true);
    
    try {
      const items = Object.entries(receivingQuantities).map(([line_item_id, quantity_received]) => ({
        line_item_id,
        quantity_received
      }));
      
      const response = await purchaseOrdersAPI.receiveItems(editingPO.id, items);
      const { purchase_order, status_changed, new_status, fully_received_notification } = response.data;
      
      // Update the PO in the list - use functional update to avoid stale closure
      setPurchaseOrders(prevPOs => prevPOs.map(po => 
        po.id === editingPO.id ? purchase_order : po
      ));
      setEditingPO(purchase_order);
      
      setReceivingMode(false);
      
      // PHASE 3A: If backend says there's pending assignment, trigger assignment modal
      if (purchase_order.has_pending_assignment) {
        // Show notification toast first
        if (fully_received_notification) {
          showFullyReceivedNotificationToast(fully_received_notification);
        }
        // Trigger assignment modal
        setAssignmentPO(purchase_order);
        setAssignmentModalOpen(true);
      } else if (status_changed) {
        toast.success(`Items received. PO status updated to "${new_status}"`);
      } else {
        toast.success('Receiving progress saved');
      }
    } catch (error) {
      const errDetail = error.response?.data?.detail || 'Failed to save receiving';
      if (errDetail.includes('Cannot transition') || errDetail.includes('Cannot set')) {
        toast.error("Status change blocked — check that receiving quantities are correct.");
      } else {
        toast.error(errDetail);
      }
    } finally {
      setReceivingSaving(false);
    }
  };

  const handleMarkAllReceived = async () => {
    if (!editingPO) return;
    setReceivingSaving(true);
    
    try {
      const response = await purchaseOrdersAPI.markAllReceived(editingPO.id);
      const { purchase_order, fully_received_notification } = response.data;
      
      setPurchaseOrders(prevPOs => prevPOs.map(po => 
        po.id === editingPO.id ? purchase_order : po
      ));
      setEditingPO(purchase_order);
      
      setReceivingMode(false);
      
      if (fully_received_notification) {
        showFullyReceivedNotificationToast(fully_received_notification);
      } else {
        toast.success('All items marked as received');
      }
      
      if (purchase_order.has_pending_assignment) {
        setAssignmentPO(purchase_order);
        setAssignmentModalOpen(true);
      }
    } catch (error) {
      const errDetail = error.response?.data?.detail || 'Failed to mark as received';
      if (errDetail.includes('Cannot transition') || errDetail.includes('Cannot set')) {
        toast.error("Can't mark as received from the current status.");
      } else {
        toast.error(errDetail);
      }
    } finally {
      setReceivingSaving(false);
    }
  };

  const cancelReceiving = () => {
    setReceivingMode(false);
    setReceivingQuantities({});
  };

  const handleUndoReceive = async () => {
    if (!editingPO) return;
    try {
      const response = await purchaseOrdersAPI.undoReceive(editingPO.id);
      const { purchase_order } = response.data;
      setPurchaseOrders(prevPOs => prevPOs.map(po =>
        po.id === editingPO.id ? purchase_order : po
      ));
      setEditingPO(purchase_order);
      toast.success(`Receiving reversed for PO ${purchase_order.po_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo receiving');
    }
  };

  // Check if PO is in a receivable status
  // Valid receivable statuses: Pending, Needs Picked Up, Partially Received
  // Receiving from Pending will auto-transition to Partially Received or Received
  // Only Rejected POs cannot be received
  const isReceivableStatus = (status) => {
    return ['Pending', 'Needs Picked Up', 'Partially Received'].includes(status);
  };

  const handleAddVendor = async (e) => {
    if (e) e.preventDefault();
    if (!newVendorName.trim()) return;
    
    setSavingVendor(true);
    try {
      const res = await vendorsAPI.create({ name: newVendorName.trim() });
      setVendors([...vendors, res.data]);
      // Auto-select new vendor in edit form
      setEditFormData(prev => ({ ...prev, vendor_id: res.data.id }));
      setNewVendorName('');
      setShowInlineVendorAdd(false);
      toast.success('Vendor added');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add vendor';
      if (errorMsg.includes('already exists')) {
        toast.error(`A vendor named "${newVendorName.trim()}" already exists. Please use a different name.`);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSavingVendor(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPO) return;
    
    setDeleting(true);
    try {
      await purchaseOrdersAPI.delete(deletingPO.id);
      toast.success('Purchase Order deleted');
      setDeleteConfirmOpen(false);
      setDeletingPO(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PO');
    } finally {
      setDeleting(false);
    }
  };

  // Fetch full PO detail (with line_items) before opening Print modal
  const openPrintModal = async (po) => {
    let fullPO = po;
    if (!po.line_items || po.line_items.length === 0) {
      try {
        const res = await purchaseOrdersAPI.get(po.id);
        fullPO = res.data;
      } catch {
        toast.error('Failed to load PO details');
        return;
      }
    }
    setPrintingPO(fullPO);
    setPrintModalOpen(true);
  };

  // Fetch full PO detail before opening Email modal (ensures vendor_email pre-fill)
  const openEmailModal = async (po) => {
    let fullPO = po;
    if (!po.line_items || po.line_items.length === 0) {
      try {
        const res = await purchaseOrdersAPI.get(po.id);
        fullPO = res.data;
      } catch {
        toast.error('Failed to load PO details');
        return;
      }
    }
    setEmailingPO(fullPO);
    setEmailModalOpen(true);
  };

  const handleExportCsv = async (exportFormat = 'detailed') => {
    try {
      const response = await purchaseOrdersAPI.exportCsv(exportFormat);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `purchase_orders_${exportFormat}_${format(new Date(), 'yyyyMMdd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const canEditPO = (po) => {
    if (isOwner) return true;
    if (hasPerm('jobs.parts.assign')) return true;
    return po.created_by_user_id === user?.id && po.status === 'Pending';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 w-full min-w-0 max-w-full overflow-hidden">
        {/* Header with New PO button */}
        <div className="flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <ShoppingCart className="w-8 h-8 text-primary shrink-0" />
            <h1 className="text-2xl font-bold font-heading truncate">Purchase Orders</h1>
            <Badge variant="outline" className="text-xs shrink-0" data-testid="po-total-badge">{poPage.total_all}</Badge>
            {(isOwner || isOffice) && (
              <POEmailIssuesIndicator refreshKey={emailIssuesKey} autoOpen={searchParams.get('emailIssues') === '1'}
                onReview={openEmailDiagnosticsFor} />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Desktop: Export + New PO */}
            {isOwner && (
              <Button onClick={() => handleExportCsv('detailed')} variant="outline" size="sm" className="hidden md:inline-flex">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            )}
            <Button onClick={() => handleDrawerOpenChange(true)} data-testid="new-po-btn" size="sm" className="hidden md:inline-flex">
              <Plus className="w-4 h-4 mr-2" />
              New PO
            </Button>
            {/* Mobile: Kebab for Export */}
            {isOwner && (
              <div className="md:hidden">
                <KebabMenu
                  items={[{ label: 'Export', icon: Download, onClick: () => handleExportCsv('detailed'), 'data-testid': 'po-export-kebab' }]}
                  data-testid="po-kebab-menu"
                />
              </div>
            )}
          </div>
        </div>

        {/* PO List Content */}
        <div className="space-y-4">
            {/* Error Banner with Retry */}
            {loadError && (
              <div className="flex items-center justify-between gap-3 p-4 bg-destructive/10 text-destructive rounded-lg border border-destructive/20">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <span>{loadError}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setLoading(true);
                    fetchData();
                  }}
                  className="shrink-0"
                >
                  <Loader2 className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : 'hidden'}`} />
                  Retry
                </Button>
              </div>
            )}

            {/* Quick Filters - Desktop */}
            {poPage.total_all > 0 && (
              <div className="hidden md:flex flex-col gap-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Filter className="w-4 h-4" />
                  <span>Filters:</span>
                </div>
                
                {/* Status Chips */}
                <div className="flex gap-1 overflow-x-auto pb-1 -mb-1">
                  <Button
                    variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setStatusFilter('all')}
                    className="h-7 text-xs shrink-0"
                  >
                    All Status
                  </Button>
                  {WARRANTY_STATUS_OPTIONS.map(status => (
                    <Button
                      key={status}
                      variant={statusFilter === status ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setStatusFilter(status)}
                      className="h-7 text-xs shrink-0 whitespace-nowrap"
                    >
                      <span className={`w-2 h-2 rounded-full mr-1.5 ${
                        status === 'Pending' ? 'bg-yellow-500' :
                        status === 'Needs Picked Up' ? 'bg-orange-500' :
                        status === 'Partially Received' ? 'bg-blue-500' :
                        status === 'Pending Assignment' ? 'bg-amber-500' :
                        status === 'Awaiting Warranty Part' ? 'bg-purple-500' : 'bg-green-500'
                      }`} />
                      {status}
                    </Button>
                  ))}
                </div>

                {/* Search filter - Phase 4: Updated placeholder */}
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="Search by tech, PO #, or job #..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="h-8 text-sm max-w-xs"
                  />
                </div>

                {/* Dropdowns row */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Vendor Dropdown */}
                  <Select value={vendorFilter} onValueChange={setVendorFilter}>
                    <SelectTrigger className="w-[140px] sm:w-[160px] h-7 text-xs" data-testid="vendor-filter">
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Warehouse Dropdown */}
                  <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                    <SelectTrigger className="w-[140px] sm:w-[160px] h-7 text-xs" data-testid="warehouse-filter">
                      <SelectValue placeholder="All Warehouses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Warehouses</SelectItem>
                      {warehouses.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Department Dropdown - Phase 4 */}
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-[140px] sm:w-[160px] h-7 text-xs">
                      <SelectValue placeholder="All Depts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      <SelectItem value="none">No Department</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Payment Terms Filter - Stage 5 */}
                  <Select value={paymentTermsFilter} onValueChange={setPaymentTermsFilter}>
                    <SelectTrigger className="w-[140px] sm:w-[160px] h-7 text-xs" data-testid="payment-terms-filter">
                      <SelectValue placeholder="All Terms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Terms</SelectItem>
                      <SelectItem value="none">No Terms</SelectItem>
                      <SelectItem value="net_15">Net 15</SelectItem>
                      <SelectItem value="net_30">Net 30</SelectItem>
                      <SelectItem value="net_45">Net 45</SelectItem>
                      <SelectItem value="net_60">Net 60</SelectItem>
                      <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>

                {/* Date Range */}
                  <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                    <SelectTrigger className="w-[120px] sm:w-[130px] h-7 text-xs">
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_RANGE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Due Date Urgency Filter */}
                  <Select value={dueDateFilter} onValueChange={setDueDateFilter}>
                    <SelectTrigger className="w-[130px] sm:w-[140px] h-7 text-xs" data-testid="due-date-filter">
                      <SelectValue placeholder="All Due Dates" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Due Dates</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="due_week">Due This Week</SelectItem>
                      <SelectItem value="due_month">Due This Month</SelectItem>
                      <SelectItem value="no_due">No Due Date</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear
                    </Button>
                  )}

                  {/* Results count */}
                  <div className="text-xs text-muted-foreground ml-auto">
                    {poPage.total}/{poPage.total_all}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Filters - Mobile */}
            {poPage.total_all > 0 && (
              <div className="md:hidden space-y-3">
                {/* Search bar + Filters button row */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search by tech, PO #, or job #..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      className="h-10 pl-9 text-sm"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 relative"
                    onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                    aria-label="Filters"
                  >
                    <Filter className="w-4 h-4" />
                    {activeFilterCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </div>

                {/* Mobile Filters Dropdown */}
                {mobileFiltersOpen && (
                  <>
                    {/* Overlay */}
                    <div 
                      className="fixed inset-0 bg-black/20 z-40"
                      onClick={() => setMobileFiltersOpen(false)}
                    />
                    
                    {/* Dropdown Panel */}
                    <div className="relative z-50 bg-background border rounded-lg shadow-lg p-4 space-y-4">
                      {/* Status Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Status</Label>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant={statusFilter === 'all' ? 'secondary' : 'outline'}
                            size="sm"
                            onClick={() => setStatusFilter('all')}
                            className="h-9 text-sm"
                          >
                            All Status
                          </Button>
                          {WARRANTY_STATUS_OPTIONS.map(status => (
                            <Button
                              key={status}
                              variant={statusFilter === status ? 'secondary' : 'outline'}
                              size="sm"
                              onClick={() => setStatusFilter(status)}
                              className="h-9 text-sm"
                            >
                              <span className={`w-2 h-2 rounded-full mr-1.5 ${
                                status === 'Pending' ? 'bg-yellow-500' :
                                status === 'Needs Picked Up' ? 'bg-orange-500' :
                                status === 'Partially Received' ? 'bg-blue-500' :
                                status === 'Pending Assignment' ? 'bg-amber-500' :
                                status === 'Awaiting Warranty Part' ? 'bg-purple-500' : 'bg-green-500'
                              }`} />
                              {status}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Vendor Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Vendor</Label>
                        <SearchableSelect
                          options={[
                            { value: 'all', label: 'All Vendors' },
                            ...vendors.map(v => ({ value: v.id, label: v.name }))
                          ]}
                          value={vendorFilter}
                          onValueChange={setVendorFilter}
                          placeholder="All Vendors"
                          searchPlaceholder="Search vendors..."
                          emptyText="No vendors found."
                          className="h-10"
                        />
                      </div>

                      {/* Warehouse Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Warehouse</Label>
                        <SearchableSelect
                          options={[
                            { value: 'all', label: 'All Warehouses' },
                            ...warehouses.map(w => ({ value: w.id, label: w.name }))
                          ]}
                          value={warehouseFilter}
                          onValueChange={setWarehouseFilter}
                          placeholder="All Warehouses"
                          searchPlaceholder="Search warehouses..."
                          emptyText="No warehouses found."
                          className="h-10"
                        />
                      </div>

                      {/* Department Filter - Phase 4 */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Department</Label>
                        <SearchableSelect
                          options={[
                            { value: 'all', label: 'All Departments' },
                            { value: 'none', label: 'No Department' },
                            ...departments.map(d => ({ value: d.id, label: d.name }))
                          ]}
                          value={departmentFilter}
                          onValueChange={setDepartmentFilter}
                          placeholder="All Departments"
                          searchPlaceholder="Search departments..."
                          emptyText="No departments found."
                          className="h-10"
                        />
                      </div>

                      {/* Payment Terms Filter - Stage 5 */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Payment Terms</Label>
                        <SearchableSelect
                          options={[
                            { value: 'all', label: 'All Terms' },
                            { value: 'none', label: 'No Terms' },
                            { value: 'net_15', label: 'Net 15' },
                            { value: 'net_30', label: 'Net 30' },
                            { value: 'net_45', label: 'Net 45' },
                            { value: 'net_60', label: 'Net 60' },
                            { value: 'due_on_receipt', label: 'Due on Receipt' },
                            { value: 'custom', label: 'Custom' },
                          ]}
                          value={paymentTermsFilter}
                          onValueChange={setPaymentTermsFilter}
                          placeholder="All Terms"
                          searchPlaceholder="Search terms..."
                          emptyText="No options."
                          className="h-10"
                          sortAlphabetically={false}
                        />
                      </div>

                      {/* Date Range Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Time Period</Label>
                        <SearchableSelect
                          options={DATE_RANGE_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                          value={dateRangeFilter}
                          onValueChange={setDateRangeFilter}
                          placeholder="All Time"
                          searchPlaceholder="Search..."
                          emptyText="No options."
                          className="h-10"
                          sortAlphabetically={false}
                        />
                      </div>

                      {/* Due Date Urgency Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Due Date</Label>
                        <SearchableSelect
                          options={[
                            { value: 'all', label: 'All Due Dates' },
                            { value: 'overdue', label: 'Overdue' },
                            { value: 'due_week', label: 'Due This Week' },
                            { value: 'due_month', label: 'Due This Month' },
                            { value: 'no_due', label: 'No Due Date' },
                          ]}
                          value={dueDateFilter}
                          onValueChange={setDueDateFilter}
                          placeholder="All Due Dates"
                          searchPlaceholder="Search..."
                          emptyText="No options."
                          className="h-10"
                          sortAlphabetically={false}
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          className="flex-1 h-10"
                          onClick={() => {
                            clearFiltersExceptSearch();
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          className="flex-1 h-10"
                          onClick={() => setMobileFiltersOpen(false)}
                        >
                          Apply
                        </Button>
                      </div>

                      {/* Results count */}
                      <div className="text-xs text-muted-foreground text-center">
                        Showing {poPage.total} of {poPage.total_all} POs
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Warehouse filter indicator */}
            {selectedWarehouseName && (
              <div
                className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md text-sm w-fit"
                data-testid="warehouse-filter-indicator"
              >
                <Warehouse className="w-3.5 h-3.5" />
                <span>Showing POs for: <span className="font-medium">{selectedWarehouseName}</span></span>
                <button
                  onClick={() => setWarehouseFilter('all')}
                  className="ml-1 hover:bg-blue-100 rounded p-0.5"
                  data-testid="warehouse-filter-indicator-clear"
                  aria-label="Clear warehouse filter"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Server-Side Drafts Section */}
            {drafts.length > 0 && (
              <Card className="border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20" data-testid="po-drafts-section">
                <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => setDraftsExpanded(!draftsExpanded)}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-800 dark:text-amber-300">
                      <FileEdit className="w-4 h-4" />
                      Drafts ({drafts.length})
                    </CardTitle>
                    <ChevronDown className={`w-4 h-4 text-amber-600 transition-transform ${draftsExpanded ? '' : '-rotate-90'}`} />
                  </div>
                </CardHeader>
                {draftsExpanded && (
                  <CardContent className="pt-0 px-4 pb-4">
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {drafts.map(draft => {
                        const fd = draft.form_data || {};
                        const lineItems = (fd.line_items || []).filter(li => (li.item_name || '').trim());
                        const totalCost = lineItems.reduce((sum, li) => sum + ((parseFloat(li.unit_cost) || 0) * (parseInt(li.quantity) || 1)), 0);
                        const isDiscarding = discardingDraftId === draft.id;
                        const isSubmitting = submittingDraftId === draft.id;
                        const canSubmit = fd.vendor_id && fd.warehouse_id && lineItems.length > 0;
                        return (
                          <div 
                            key={draft.id} 
                            className="border border-amber-200 dark:border-amber-800 rounded-lg p-3 bg-white dark:bg-card hover:shadow-sm transition-shadow"
                            data-testid={`draft-card-${draft.id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <TruncatedTextWithTooltip
                                  as="p"
                                  text={draft.title}
                                  className="text-sm font-medium"
                                  data-testid={`draft-title-${draft.id}`}
                                />
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  {draft.vendor_name && (
                                    <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                                      <Tag className="w-3 h-3" />{draft.vendor_name}
                                    </span>
                                  )}
                                  {draft.warehouse_name && (
                                    <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                                      <Warehouse className="w-3 h-3" />{draft.warehouse_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Badge variant="outline" className="text-[10px] shrink-0 border-amber-300 text-amber-700 dark:text-amber-400">
                                DRAFT
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-2.5">
                              <span>{draft.item_count} item{draft.item_count !== 1 ? 's' : ''}</span>
                              {totalCost > 0 && <span>${totalCost.toFixed(2)}</span>}
                              <span className="ml-auto">{draft.created_by_name}</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground/70 mb-2.5">
                              Updated {draft.updated_at ? new Date(draft.updated_at).toLocaleString() : 'N/A'}
                              {draft.updated_by_name && draft.updated_by_user_id !== draft.created_by_user_id && ` by ${draft.updated_by_name}`}
                            </p>
                            <div className="flex gap-1.5">
                              <Button
                                size="sm" variant="outline" className="text-xs h-7 flex-1"
                                onClick={() => handleEditDraft(draft)}
                                data-testid={`draft-resume-${draft.id}`}
                              >
                                Resume
                              </Button>
                              <Button
                                size="sm" variant="default" className="text-xs h-7 flex-1"
                                disabled={!canSubmit || isSubmitting}
                                onClick={() => handleSubmitDraftFromList(draft.id)}
                                data-testid={`draft-submit-${draft.id}`}
                              >
                                {isSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Submit'}
                              </Button>
                              <KebabMenu
                                actions={[
                                  { label: 'Duplicate', onClick: () => handleDuplicateDraft(draft.id) },
                                  { label: 'Discard', onClick: () => handleDiscardDraftFromList(draft.id), variant: 'destructive' },
                                ]}
                                disabled={isDiscarding}
                                data-testid={`draft-menu-${draft.id}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {refreshFailed && !poBusy && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300" data-testid="po-refresh-failed-top">
                Couldn't refresh the list, showing what was last loaded.{' '}
                <button type="button" className="underline" onClick={() => fetchPOs({ fresh: true })} data-testid="po-refresh-retry-top">Retry</button>
              </div>
            )}
            {poPage.total_all === 0 && !poBusy ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No purchase orders yet</p>
                  <Button 
                    variant="link" 
                    onClick={() => handleDrawerOpenChange(true)}
                    className="mt-2"
                  >
                    Create your first PO
                  </Button>
                </CardContent>
              </Card>
            ) : poPage.total === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12" data-testid="po-no-matches">
                  <Filter className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No POs match current filters</p>
                  <Button 
                    variant="link" 
                    onClick={clearFilters}
                    className="mt-2"
                  >
                    Clear filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  {/* Desktop table — hidden on mobile */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <SortableTableHead sortKey={poSortKey} sortDir={poSortDir} columnKey="po_number" onSort={poOnSort} className="text-left p-3">Details</SortableTableHead>
                          <SortableTableHead sortKey={poSortKey} sortDir={poSortDir} columnKey="total_items" onSort={poOnSort} className="text-center p-3 w-16">Items</SortableTableHead>
                          {canSeeCosts && <SortableTableHead sortKey={poSortKey} sortDir={poSortDir} columnKey="total" onSort={poOnSort} className="text-right p-3 w-24">Total</SortableTableHead>}
                          <SortableTableHead sortKey={poSortKey} sortDir={poSortDir} columnKey="payment_due_date" onSort={poOnSort} className="text-center p-3 w-28">Due Date</SortableTableHead>
                          <th className="text-center p-3 font-medium w-28">Received</th>
                          <SortableTableHead sortKey={poSortKey} sortDir={poSortDir} columnKey="status" onSort={poOnSort} className="text-center p-3 w-36">Status</SortableTableHead>
                          <th className="text-right p-3 font-medium w-32">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {purchaseOrders.map((po) => (
                          <tr key={po.id} className="hover:bg-muted/30">
                            {/* Stacked Details cell */}
                            <td className="p-3">
                              {/* Line 1: PO # (dominant) + Date (muted) + Badges */}
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="text-base font-mono font-semibold">{po.po_number}</span>
                                <span className="text-sm text-muted-foreground">
                                  {formatInWarehouseTimezone(po.submitted_at, { timezone: po.warehouse_timezone }, {
                                    includeTime: false,
                                    showTimezone: false,
                                    dateStyle: 'medium'
                                  })}
                                </span>
                                {po.source === 'consignment' && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-amber-50 text-amber-700 border-amber-200">
                                    Auto
                                  </Badge>
                                )}
                                {po.is_warranty && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-purple-50 text-purple-700 border-purple-200">
                                    <Shield className="w-3 h-3 mr-1" />
                                    Warranty
                                  </Badge>
                                )}
                                {po.is_warranty && po.status === 'Awaiting Warranty Part' && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-orange-50 text-orange-700 border-orange-200">
                                    Awaiting Part
                                  </Badge>
                                )}
                                {po.is_warranty && po.warranty_credit_received === false && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-red-50 text-red-700 border-red-200">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Awaiting Credit
                                  </Badge>
                                )}
                              </div>
                              {/* Line 2: Vendor (secondary, but readable) */}
                              <div className="text-sm font-medium text-foreground mt-0.5">
                                {po.vendor_name || '—'}
                              </div>
                              {/* Line 3: Warehouse · Dept · Job · Employee (lightest) */}
                              {(() => {
                                const parts = [
                                  po.warehouse_name ? (po.warehouse_name + (po.warehouse_code ? ` (${po.warehouse_code})` : '')) : null,
                                  po.department_name || null,
                                  (po.job_name || po.job_number) ? ((po.job_id ? '🔗 ' : '') + (po.job_name || '') + (po.job_number ? ` #${po.job_number}` : '')) : null,
                                  po.created_by_name || null,
                                ].filter(Boolean);
                                return parts.length > 0 ? (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {parts.join(' · ')}
                                  </div>
                                ) : null;
                              })()}
                            </td>
                            <td className="p-3 text-center">{formatQty(po.total_items)}</td>
                            {canSeeCosts && (
                              <td className="p-3 text-right font-medium whitespace-nowrap">
                                {po.total ? `$${po.total.toFixed(2)}` : '-'}
                              </td>
                            )}
                            <td className="p-3 text-center" data-testid={`po-due-date-${po.id}`}>
                              {(() => {
                                if (!po.payment_due_date) return <span className="text-xs text-muted-foreground">—</span>;
                                const due = new Date(po.payment_due_date + 'T00:00:00');
                                const now = new Date(); now.setHours(0,0,0,0);
                                const diff = Math.ceil((due - now) / 86400000);
                                let cls, label;
                                if (diff < 0) { cls = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'; label = `${Math.abs(diff)}d overdue`; }
                                else if (diff <= 7) { cls = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'; label = diff === 0 ? 'Due today' : `${diff}d left`; }
                                else { cls = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'; label = `${diff}d left`; }
                                const fmtDate = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                return (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className={`inline-block text-[11px] font-medium px-1.5 py-0.5 rounded cursor-help ${cls}`}>
                                          {fmtDate} · {label}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs max-w-[220px]">
                                        <p>Due {fmtDate} based on {po.vendor_payment_terms || 'vendor terms'} from PO date.</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              })()}
                            </td>
                            {/* Date Received */}
                            <td className="p-3 text-center" data-testid={`po-date-received-${po.id}`}>
                              {(() => {
                                if (!po.date_received) return <span className="text-xs text-muted-foreground">—</span>;
                                const dt = new Date(po.date_received);
                                const fmtDate = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                const isPartial = po.date_received_label === 'partial';
                                return (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-xs font-medium">{fmtDate}</span>
                                    {isPartial && <span className="text-[10px] text-amber-600 font-medium">Partial</span>}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <Badge className={STATUS_COLORS[po.status]}>
                                  {po.status}
                                </Badge>
                                {/* P2: Receiving progress indicator */}
                                {isReceivableStatus(po.status) && po.total_quantity_ordered > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    {formatQty(po.total_quantity_received)}/{formatQty(po.total_quantity_ordered)} received
                                  </div>
                                )}
                                {/* Phase 4: Enhanced auto-rejection UX clarification */}
                                {po.auto_rejected && po.status === 'Rejected' && (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      Spend limit exceeded
                                    </span>
                                    {canSeeCosts && (
                                      <span className="text-xs text-muted-foreground">
                                        Office can override
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex justify-end gap-1">
                                {isReceivableStatus(po.status) && canReceiveAndAssign && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); openReceiveAndAssign(po); }}
                                    title="Receive items, choose a warehouse and optionally allocate to a job in one step"
                                    data-testid={`receive-assign-${po.id}`}
                                    className="h-8 px-2.5 text-xs font-medium gap-1"
                                  >
                                    <PackageCheck className="w-3.5 h-3.5" />
                                    Receive & Assign
                                  </Button>
                                )}
                                {/* Receive Items button - for receivable status POs */}
                                {isReceivableStatus(po.status) && (isOwner || isOffice || hasPerm('jobs.parts.assign')) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openReceiveModal(po)}
                                    title="Receive Items"
                                    data-testid={`receive-items-${po.id}`}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  >
                                    <Package className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* ⚡ Receive all & assign */}
                                {isReceivableStatus(po.status) && (isOwner || isOffice || hasPerm('jobs.parts.assign')) && (
                                  <Button
                                    variant="default"
                                    size="icon"
                                    onClick={() => openQuickReceive(po)}
                                    title="Receive all & assign"
                                    disabled={quickReceiveBusy}
                                    data-testid={`quick-receive-${po.id}`}
                                    className="h-8 w-8 bg-amber-500 hover:bg-amber-600 text-white"
                                  >
                                    <Zap className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Assign Inventory button - for Pending Assignment POs */}
                                {po.has_pending_assignment && (isOwner || isOffice || hasPerm('jobs.parts.assign')) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAssignmentPO(po);
                                      setAssignmentModalOpen(true);
                                    }}
                                    title="Assign received items to a warehouse or vehicle"
                                    data-testid={`assign-inventory-${po.id}`}
                                    className="h-7 px-2.5 text-xs font-medium border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800 gap-1"
                                  >
                                    <ArrowRight className="w-3.5 h-3.5" />
                                    Assign
                                  </Button>
                                )}
                                {/* Email button - visible to office/admin who can see costs */}
                                {canSeeCosts && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEmailModal(po)}
                                    title="Email to Vendor"
                                    data-testid={`email-po-${po.id}`}
                                  >
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Print button - visible to office/admin who can see costs */}
                                {canSeeCosts && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openPrintModal(po)}
                                    title="Print PO"
                                    data-testid={`print-po-${po.id}`}
                                  >
                                    <Printer className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Activity log button */}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setActivityPO(po)}
                                  title="Activity Log"
                                  data-testid={`activity-po-${po.id}`}
                                >
                                  <ClipboardList className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDocumentsPO(po)}
                                  title={po.document_count ? `${po.document_count} document${po.document_count === 1 ? '' : 's'}` : 'Documents'}
                                  className="relative"
                                  data-testid={`documents-po-${po.id}`}
                                >
                                  <Paperclip className="w-4 h-4" />
                                  {po.document_count > 0 && (
                                    <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground" data-testid={`po-doc-count-${po.id}`}>
                                      {po.document_count}
                                    </span>
                                  )}
                                </Button>
                                {canEditPO(po) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditModal(po)}
                                    data-testid={`edit-po-${po.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {isOwner && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setDeletingPO(po);
                                      setDeleteConfirmOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Stacked compact rows */}
                  <div className="sm:hidden divide-y" data-testid="po-mobile-list">
                    {purchaseOrders.map((po) => {
                      const kebabItems = [
                        ...(isReceivableStatus(po.status) && canReceiveAndAssign
                          ? [{ label: 'Receive & Assign', icon: PackageCheck, onClick: () => openReceiveAndAssign(po), 'data-testid': `mobile-receive-assign-${po.id}` }]
                          : []),
                        ...(isReceivableStatus(po.status) && (isOwner || isOffice || hasPerm('jobs.parts.assign'))
                          ? [
                              { label: 'Receive Items', icon: Package, onClick: () => openReceiveModal(po), 'data-testid': `mobile-receive-${po.id}` },
                              { label: 'Receive all & assign', icon: Zap, onClick: () => openQuickReceive(po), 'data-testid': `mobile-quick-receive-${po.id}` },
                            ]
                          : []),
                        ...(po.has_pending_assignment && (isOwner || isOffice || hasPerm('jobs.parts.assign'))
                          ? [{ label: 'Assign Inventory', icon: ArrowRight, onClick: () => { setAssignmentPO(po); setAssignmentModalOpen(true); }, 'data-testid': `mobile-assign-${po.id}` }]
                          : []),
                        ...(canSeeCosts
                          ? [
                              { label: 'Email Vendor', icon: Mail, onClick: () => openEmailModal(po), 'data-testid': `mobile-email-${po.id}` },
                              { label: 'Print', icon: Printer, onClick: () => openPrintModal(po), 'data-testid': `mobile-print-${po.id}` },
                            ]
                          : []),
                        { label: 'Documents', icon: Paperclip, onClick: () => setDocumentsPO(po), 'data-testid': `mobile-documents-${po.id}` },
                        ...(canEditPO(po)
                          ? [{ label: 'Edit', icon: Edit2, onClick: () => openEditModal(po), 'data-testid': `mobile-edit-${po.id}` }]
                          : []),
                        ...(isOwner
                          ? [{ label: 'Delete', icon: Trash2, onClick: () => { setDeletingPO(po); setDeleteConfirmOpen(true); }, 'data-testid': `mobile-delete-${po.id}` }]
                          : []),
                      ];

                      return (
                        <div
                          key={po.id}
                          className="flex items-start gap-3 px-3 py-2.5 active:bg-muted/50 cursor-pointer"
                          onClick={() => openEditModal(po)}
                          data-testid={`po-mobile-row-${po.id}`}
                        >
                          {/* Main content */}
                          <div className="flex-1 min-w-0">
                            {/* Line 1: PO # + Status pill + special badges */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-mono font-semibold" data-testid={`po-mobile-number-${po.id}`}>{po.po_number}</span>
                              {po.document_count > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground" data-testid={`po-mobile-doc-count-${po.id}`}>
                                  <Paperclip className="h-3 w-3" />{po.document_count}
                                </span>
                              )}
                              <Badge className={`${STATUS_COLORS[po.status]} text-[11px] px-1.5 py-0 h-5 leading-tight`} data-testid={`po-mobile-status-${po.id}`}>
                                {po.status}
                              </Badge>
                              {po.source === 'consignment' && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-amber-50 text-amber-700 border-amber-200">Auto</Badge>
                              )}
                              {po.is_warranty && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-purple-50 text-purple-700 border-purple-200">
                                  <Shield className="w-2.5 h-2.5 mr-0.5" />Warranty
                                </Badge>
                              )}
                              {po.is_warranty && po.warranty_credit_received === false && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-red-50 text-red-700 border-red-200">
                                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Credit
                                </Badge>
                              )}
                            </div>
                            {/* Line 2: Vendor + cost */}
                            <div className="flex items-baseline gap-2 mt-0.5">
                              <TruncatedTextWithTooltip
                                text={po.vendor_name || '\u2014'}
                                className="text-sm text-foreground"
                              />
                              {canSeeCosts && po.total ? (
                                <span className="text-xs text-muted-foreground font-medium shrink-0">${po.total.toFixed(2)}</span>
                              ) : null}
                            </div>
                            {/* Line 3: Date · Warehouse (compact) · Job · Tech */}
                            {(() => {
                              let wLabel = po.warehouse_name || null;
                              if (po.warehouse_code && po.warehouse_name) {
                                const suffix = po.warehouse_name.split(/[-_]/).pop().replace(/\s*\(.*\)$/, '').trim();
                                wLabel = suffix ? `${suffix} (${po.warehouse_code})` : po.warehouse_code;
                              } else if (po.warehouse_code) {
                                wLabel = po.warehouse_code;
                              }
                              const parts = [
                                formatInWarehouseTimezone(po.submitted_at, { timezone: po.warehouse_timezone }, {
                                  includeTime: false, showTimezone: false, dateStyle: 'medium'
                                }),
                                wLabel,
                                (po.job_name || po.job_number) ? ((po.job_id ? '🔗 ' : '') + (po.job_name || '') + (po.job_number ? ` #${po.job_number}` : '')) : null,
                                po.created_by_name || null,
                              ].filter(Boolean);
                              return parts.length > 0 ? (
                                <TruncatedTextWithTooltip
                                  as="div"
                                  text={parts.join(' \u00B7 ')}
                                  className="text-xs text-muted-foreground mt-0.5"
                                />
                              ) : null;
                            })()}
                            {/* Indicators */}
                            {isReceivableStatus(po.status) && po.total_quantity_ordered > 0 && (
                              <div className="text-[11px] text-muted-foreground mt-0.5">
                                {formatQty(po.total_quantity_received)}/{formatQty(po.total_quantity_ordered)} received
                              </div>
                            )}
                            {/* Due Date - mobile */}
                            {po.payment_due_date && (() => {
                              const due = new Date(po.payment_due_date + 'T00:00:00');
                              const now = new Date(); now.setHours(0,0,0,0);
                              const diff = Math.ceil((due - now) / 86400000);
                              let cls, label;
                              if (diff < 0) { cls = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'; label = `${Math.abs(diff)}d overdue`; }
                              else if (diff <= 7) { cls = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'; label = diff === 0 ? 'Due today' : `${diff}d left`; }
                              else { cls = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'; label = `${diff}d left`; }
                              return (
                                <span className={`inline-block text-[10px] font-medium px-1 py-0 rounded mt-0.5 ${cls}`} data-testid={`po-due-mobile-${po.id}`}>
                                  {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {label}
                                </span>
                              );
                            })()}
                            {po.auto_rejected && po.status === 'Rejected' && (
                              <span className="text-[11px] text-amber-600 font-medium inline-flex items-center gap-0.5 mt-0.5">
                                <AlertCircle className="w-3 h-3" />Spend limit exceeded
                              </span>
                            )}
                            {/* Inline Assign button for Pending Assignment POs */}
                            {po.has_pending_assignment && (isOwner || isOffice || hasPerm('jobs.parts.assign')) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAssignmentPO(po);
                                  setAssignmentModalOpen(true);
                                }}
                                data-testid={`mobile-assign-btn-${po.id}`}
                                className="mt-1.5 h-6 px-2 text-[11px] font-medium border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 gap-1 w-fit"
                              >
                                <ArrowRight className="w-3 h-3" />
                                Assign
                              </Button>
                            )}
                          </div>

                          {/* Right: Items badge + Kebab */}
                          <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                            <span
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted/80 text-[11px] font-medium text-muted-foreground"
                              title={`${formatQty(po.total_items)} items`}
                              data-testid={`po-mobile-items-${po.id}`}
                            >
                              {formatQty(po.total_items)}
                            </span>
                            {kebabItems.length > 0 && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <KebabMenu
                                  items={kebabItems}
                                  data-testid={`po-mobile-kebab-${po.id}`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t px-3 pb-20 md:pb-0">
                    <POPagination page={page} pages={poPage.pages} pageSize={pageSize} total={poPage.total} busy={poBusy}
                      stale={refreshFailed} onRetry={() => fetchPOs({ fresh: true })}
                      onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} onPageSize={setPageSize} />
                  </div>
                </CardContent>
              </Card>
            )}
        </div>
      </div>

      {/* New PO Drawer */}
      <NewPODrawer
        open={newPODrawerOpen}
        onOpenChange={handleDrawerOpenChange}
        vendors={vendors}
        departments={departments}
        onPOCreated={(newPO) => {
          fetchData();
          // Toast is already shown in NewPODrawer (including auto-reject warning)
        }}
        onVendorAdded={(vendor) => setVendors(prev => [...prev, vendor])}
        prefillData={prefillData}
        onPrefillConsumed={() => setPrefillData(null)}
        editingDraft={editingDraft}
        onDraftSaved={handleDraftSaved}
        onDraftSubmitted={handleDraftSubmitted}
      />

      {/* Edit Purchase Order Drawer */}
      <AppDrawer
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        title={`Edit Purchase Order ${editingPO?.po_number || ''}`}
        description="Update purchase order details, line items, and status"
        size="lg"
        isDirty={false}
        data-testid="edit-po-drawer"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="edit-po-form" disabled={editSaving} data-testid="save-po-btn">
              {editSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </>
        }
      >
          {/* Phase 4: Enhanced auto-rejection UX clarification */}
          {editingPO?.auto_rejected && editingPO?.status === 'Rejected' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Auto-Rejected Due to Spend Limit</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">{editingPO.rejection_reason}</p>
              {canSeeCosts && (
                <p className="text-sm text-amber-600 mt-2 font-medium">
                  As office staff, you can override this rejection by changing the status below.
                </p>
              )}
            </div>
          )}
          {/* PHASE 3A: Pending Assignment alert banner — driven by has_pending_assignment (quantity-driven, not status) */}
          {editingPO?.has_pending_assignment && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Assignment Pending</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                All items have been received. Assign inventory to a warehouse or vehicle to complete this PO.
              </p>
              <Button
                type="button"
                size="sm"
                className="mt-2 bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  setAssignmentPO(editingPO);
                  setAssignmentModalOpen(true);
                }}
                data-testid="assign-inventory-from-banner"
              >
                Assign Inventory Now
              </Button>
            </div>
          )}
          {editFormData && (
            <form id="edit-po-form" onSubmit={handleEditSubmit}>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Vendor */}
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SearchableSelect
                          options={vendors.map(v => ({ value: v.id, label: v.name }))}
                          value={editFormData.vendor_id}
                          onValueChange={(value) => setEditFormData({ ...editFormData, vendor_id: value })}
                          placeholder="Select vendor..."
                          searchPlaceholder="Search vendors..."
                          emptyText="No vendors found."
                          autoOpenOnFocus={false}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => { setShowInlineVendorAdd(v => !v); setNewVendorName(''); }}
                        data-testid="edit-po-add-vendor-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {showInlineVendorAdd && (
                      <div className="flex gap-2 items-end mt-2 p-3 bg-muted/50 rounded-lg border" data-testid="edit-po-inline-vendor-form">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs">New Vendor Name</Label>
                          <Input
                            value={newVendorName}
                            onChange={(e) => setNewVendorName(e.target.value)}
                            placeholder="Enter vendor name"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddVendor(e); } }}
                            autoFocus
                            data-testid="edit-po-new-vendor-input"
                          />
                        </div>
                        <Button type="button" size="sm" disabled={savingVendor || !newVendorName.trim()} onClick={handleAddVendor} data-testid="edit-po-save-vendor-btn">
                          {savingVendor && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                          Add
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setShowInlineVendorAdd(false); setNewVendorName(''); }}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Department - Phase 4 */}
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select
                      value={editFormData.department_id}
                      onValueChange={(value) => setEditFormData({ ...editFormData, department_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department (optional)..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {departments.map(d => (
                          <SelectItem key={d.id} value={d.id}>
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-muted-foreground" />
                              <span>{d.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Job Reference - Smart selector based on CRM */}
                  {hasCrm && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5">
                        <Link2 className="w-3.5 h-3.5" />
                        Link to CRM Job
                      </Label>
                      {editSelectedJob ? (
                        <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30" data-testid="edit-po-selected-job">
                          <div className="flex-1 min-w-0">
                            <TruncatedTextWithTooltip
                              as="p"
                              text={editSelectedJob.job_name || editSelectedJob.job_number}
                              className="text-sm font-medium"
                            />
                            <p className="text-xs text-muted-foreground">#{editSelectedJob.job_number}</p>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => {
                            setEditSelectedJob(null);
                            setEditFormData(prev => ({ ...prev, job_id: '', job_name: '', job_number: '' }));
                          }}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <Input
                            value={editJobSearchQuery}
                            onChange={(e) => { setEditJobSearchQuery(e.target.value); setEditJobSearchOpen(true); }}
                            onFocus={() => setEditJobSearchOpen(true)}
                            placeholder="Search CRM jobs..."
                            data-testid="edit-po-job-search"
                          />
                          {editJobSearching && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />}
                          {editJobSearchOpen && editJobSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {editJobSearchResults.map(j => (
                                <button
                                  key={j.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                                  onClick={() => {
                                    setEditSelectedJob(j);
                                    setEditFormData(prev => ({
                                      ...prev,
                                      job_id: j.id,
                                      job_name: j.job_name || '',
                                      job_number: j.job_number || '',
                                    }));
                                    setEditJobSearchOpen(false);
                                    setEditJobSearchQuery('');
                                  }}
                                >
                                  <span className="font-medium">{j.job_name || j.job_number}</span>
                                  <span className="text-muted-foreground ml-2">#{j.job_number}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Job Name */}
                  <div className="space-y-2">
                    <Label>{hasCrm ? 'Job Name (text fallback)' : 'Job Name'}</Label>
                    <Input
                      value={editFormData.job_name}
                      onChange={(e) => setEditFormData({ ...editFormData, job_name: e.target.value })}
                      disabled={!!editSelectedJob}
                    />
                  </div>

                  {/* Job Number */}
                  <div className="space-y-2">
                    <Label>{hasCrm ? 'Job # (text fallback)' : 'Job Number'}</Label>
                    <Input
                      value={editFormData.job_number}
                      onChange={(e) => setEditFormData({ ...editFormData, job_number: e.target.value })}
                      disabled={!!editSelectedJob}
                    />
                  </div>

                  {/* Order Description */}
                  <div className="space-y-2 md:col-span-2">
                    <Label>Order Description</Label>
                    <Textarea
                      value={editFormData.order_description}
                      onChange={(e) => setEditFormData({ ...editFormData, order_description: e.target.value })}
                      rows={2}
                    />
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addEditLineItem}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {/* Header row */}
                    <div className="flex gap-2 items-center text-xs text-muted-foreground font-medium">
                      <div className="flex-1 min-w-0">Item Name</div>
                      <div className="w-20 flex-shrink-0 text-center">Qty</div>
                      {isOwner && <div className="w-24 flex-shrink-0 text-center">Unit Cost</div>}
                      <div className="w-9"></div>
                    </div>
                    {editFormData.line_items.map((item, index) => {
                      return (
                        <div key={index} className="flex gap-2 items-start">
                          <div className="flex-1 min-w-0">
                            <ItemSearchCombobox
                              value={item.item_name}
                              itemId={item.item_id}
                              onChange={(value) => updateEditLineItem(index, 'item_name', value)}
                              onSelect={(selection) => handleEditItemSelect(index, selection)}
                              placeholder="Search parts or type new..."
                              data-testid={`edit-po-line-item-${index}`}
                            />
                            {item.auto_created_item && item.item_id && (
                              <a
                                href={`/catalogue?highlight=${item.item_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors"
                                title="This part was added to your catalogue during PO receipt. Click to view in catalogue."
                                data-testid={`new-part-badge-edit-${index}`}
                              >
                                <Package className="w-3 h-3" />New Part Added
                              </a>
                            )}
                          </div>
                          <div className="w-20 flex-shrink-0">
                            <IntegerQuantityInput
                              min={1}
                              value={item.quantity}
                              onChange={(val) => updateEditLineItem(index, 'quantity', val)}
                              className="text-center"
                              data-testid={`edit-po-line-qty-${index}`}
                            />
                          </div>
                          {canSeeCosts && (
                            <div className="w-24 flex-shrink-0">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.unit_cost}
                                onChange={(e) => updateEditLineItem(index, 'unit_cost', e.target.value)}
                                placeholder="$0.00"
                                className="text-right"
                              />
                            </div>
                          )}
                          {editFormData.line_items.length > 1 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeEditLineItem(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          ) : <div className="w-9" />}
                        </div>
                      );
                    })}
                    {/* PO Total - Owner only */}
                    {canSeeCosts && (() => {
                      const poTotal = editFormData.line_items.reduce((sum, item) => {
                        if (item.quantity && item.unit_cost) {
                          return sum + (parseInt(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
                        }
                        return sum;
                      }, 0);
                      return poTotal > 0 ? (
                        <div className="flex justify-end items-center gap-2 pt-2 border-t mt-2">
                          <span className="text-sm font-medium text-muted-foreground">Total:</span>
                          <span className="text-base font-bold text-primary">${poTotal.toFixed(2)}</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Stuck-email recovery banner — only owner/office can resend.
                    Visible only when status='Received' AND email never sent.
                    Lives OUTSIDE the receiving section because isReceivableStatus
                    excludes 'Received' (a fully-received PO is no longer "receivable"). */}
                {editingPO && (isOwner || isOffice) && editingPO.status === 'Received' && !editingPO.fully_received_email_sent_at && (
                  <div className="border-t pt-4">
                    <div
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20"
                      data-testid="po-resend-email-banner"
                    >
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                          "PO Fully Received" email was not sent
                        </p>
                        <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">
                          The original notification didn't go out. Click below to retry sending it now.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 w-full sm:w-auto shrink-0"
                        disabled={resendingEmail}
                        onClick={handleResendFullyReceivedEmail}
                        data-testid="po-resend-email-btn"
                      >
                        {resendingEmail ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</>
                        ) : (
                          <><Mail className="w-3.5 h-3.5 mr-1.5" /> Resend Fully-Received Email</>
                        )}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 w-full sm:w-auto shrink-0"
                        onClick={handleShowEmailDiagnostics}
                        data-testid="po-email-diagnostics-btn"
                        title="Show resolved recipients and why the send may be failing"
                      >
                        Why is this stuck?
                      </Button>
                    </div>
                  </div>
                )}

                {editingPO && (isOwner || isOffice) && ['Received', 'Partially Received', 'Pending Assignment'].includes(editingPO.status)
                  && !(editingPO.status === 'Received' && !editingPO.fully_received_email_sent_at) && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-xs text-muted-foreground"
                      onClick={handleShowEmailDiagnostics}
                      data-testid="po-notification-history-btn"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Notification history
                    </Button>
                  </div>
                )}

                {/* P2: Receiving Section - Show for receivable statuses */}
                {(isOwner || isOffice || hasPerm('jobs.parts.assign')) && editingPO && isReceivableStatus(editingPO.status) && (
                  <div className="border-t pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Receiving
                      </p>
                      {!receivingMode ? (
                        <div className="flex gap-2">
                          {canReceiveAndAssign && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => openReceiveAndAssign(editingPO)}
                              data-testid="edit-receive-assign-btn"
                            >
                              <PackageCheck className="w-4 h-4 mr-1.5" />
                              Receive & Assign
                            </Button>
                          )}
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            onClick={initializeReceiving}
                            data-testid="start-receiving-btn"
                          >
                            Start Receiving
                          </Button>
                          <Button 
                            type="button" 
                            variant="default" 
                            size="sm" 
                            onClick={handleMarkAllReceived}
                            data-testid="mark-all-received-btn"
                          >
                            Mark All Received
                          </Button>
                          {editingPO.line_items?.some(li => (li.quantity_received || 0) > 0) && (isOwner || isOffice) && (
                            editingPO.line_items?.some(li => Number(li.quantity_assigned || 0) > 0) ? (
                              <span
                                className="text-xs text-muted-foreground self-center"
                                data-testid="undo-receive-locked-note"
                              >
                                Undo unavailable — stock already assigned
                              </span>
                            ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={handleUndoReceive}
                              data-testid="undo-receive-btn"
                            >
                              Undo Receiving
                            </Button>
                            )
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleReceiveAll}
                            data-testid="receive-all-btn"
                          >
                            Receive All
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            onClick={cancelReceiving}
                            data-testid="cancel-receiving-btn"
                          >
                            Cancel
                          </Button>
                          <Button 
                            type="button" 
                            size="sm" 
                            onClick={handleSaveReceiving}
                            disabled={receivingSaving}
                            data-testid="save-receiving-btn"
                          >
                            {receivingSaving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Receiving Progress */}
                    {!receivingMode && (
                      <div className="bg-muted/50 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm">Receiving Progress</span>
                          <span className="text-sm font-medium">
                            {formatQty(editingPO.total_quantity_received)} / {formatQty(editingPO.total_quantity_ordered)} items
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${editingPO.receiving_complete ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ 
                              width: `${editingPO.total_quantity_ordered ? 
                                Math.min(100, (editingPO.total_quantity_received / editingPO.total_quantity_ordered) * 100) : 0}%` 
                            }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Line Item Receiving Grid */}
                    {receivingMode && (
                      <div className="border rounded divide-y max-h-64 overflow-y-auto">
                        {editingPO.line_items.map((li) => {
                          const receivedQty = receivingQuantities[li.id] || 0;
                          const isFullyReceived = receivedQty >= coerceQty(li.quantity);
                          
                          return (
                            <div key={li.id} className={`p-3 ${isFullyReceived ? 'bg-green-50' : ''}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <TruncatedTextWithTooltip
                                      as="p"
                                      text={li.item_name}
                                      className="font-medium"
                                    />
                                    {li.auto_created_item && li.item_id && (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium bg-sky-100 text-sky-700 whitespace-nowrap shrink-0">
                                        <Package className="w-3 h-3" />New
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Ordered: {formatQty(li.quantity)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2">
                                    <Label className="text-sm whitespace-nowrap">Received:</Label>
                                    <IntegerQuantityInput
                                      min={0}
                                      max={li.quantity}
                                      value={receivedQty}
                                      onChange={(val) => updateReceivingQuantity(li.id, val)}
                                      className="w-20 text-center"
                                      data-testid={`receive-qty-${li.id}`}
                                    />
                                    <span className="text-sm text-muted-foreground">/ {formatQty(li.quantity)}</span>
                                  </div>
                                  {isFullyReceived && (
                                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Non-edit mode: Show current receiving status */}
                    {!receivingMode && editingPO.line_items.some(li => li.quantity_received > 0) && (
                      <div className="border rounded divide-y max-h-48 overflow-y-auto">
                        {editingPO.line_items.map((li) => (
                          <div key={li.id} className={`p-2 flex items-center justify-between text-sm ${li.is_fully_received ? 'bg-green-50' : ''}`}>
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <TruncatedTextWithTooltip text={li.item_name} />
                              {li.auto_created_item && li.item_id && (
                                <a
                                  href={`/catalogue?highlight=${li.item_id}`}
                                  onClick={(e) => { e.stopPropagation(); }}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors whitespace-nowrap shrink-0"
                                  title="This part was added to your catalogue during PO receipt. Click to view."
                                  data-testid={`new-part-badge-${li.id}`}
                                >
                                  <Package className="w-3 h-3" />New Part
                                </a>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              <span className={li.is_fully_received ? 'text-green-700 font-medium' : 'text-amber-600'}>
                                {formatQty(li.quantity_received)} / {formatQty(li.quantity)}
                              </span>
                              {li.is_fully_received ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-amber-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Office Fields - Owner only */}
                {isOwner && (
                  <div className="border-t pt-4 space-y-4">
                    <p className="text-sm font-medium text-muted-foreground">Office Use Only</p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Order Cost ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.office_cost}
                          onChange={(e) => setEditFormData({ ...editFormData, office_cost: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Expected Billable Value ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.expected_billable_value}
                          onChange={(e) => setEditFormData({ ...editFormData, expected_billable_value: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={editFormData.status}
                          onValueChange={handleStatusChange}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Show warranty-specific status only for warranty POs */}
                            {(editingPO?.is_warranty ? WARRANTY_STATUS_OPTIONS : BASE_STATUS_OPTIONS).map((status) => (
                              <SelectItem key={status} value={status}>{status}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Office Notes</Label>
                        <Textarea
                          value={editFormData.office_notes}
                          onChange={(e) => setEditFormData({ ...editFormData, office_notes: e.target.value })}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
          {editingPO?.id && <div className="mt-4"><PODocumentsPanel poId={editingPO.id} source="detail" /></div>}
      </AppDrawer>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Purchase Order</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete PO #{deletingPO?.po_number}? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Email Diagnostics Dialog — owner/office support tool */}
      <Dialog open={!!emailDiagnostics} onOpenChange={(open) => !open && setEmailDiagnostics(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="po-email-diagnostics-dialog">
          <DialogHeader>
            <DialogTitle>Email Notification Diagnostics</DialogTitle>
          </DialogHeader>
          {emailDiagnostics && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border bg-slate-50 dark:bg-slate-900/40 p-3 space-y-1">
                <div><span className="text-muted-foreground">PO:</span> <span className="font-medium">{emailDiagnostics.po_number}</span></div>
                <div><span className="text-muted-foreground">Status:</span> {emailDiagnostics.status}</div>
                <div><span className="text-muted-foreground">Sent at:</span> {emailDiagnostics.fully_received_email_sent_at ? new Date(emailDiagnostics.fully_received_email_sent_at + 'Z').toLocaleString() : <span className="text-amber-600">never</span>}</div>
                <div><span className="text-muted-foreground">Warehouse:</span> {emailDiagnostics.warehouse_name || '—'}</div>
                <div><span className="text-muted-foreground">Job #:</span> {emailDiagnostics.job_number || '—'} {emailDiagnostics.job_number_required && <span className="text-xs text-amber-600">(required)</span>}</div>
                <div><span className="text-muted-foreground">SMTP configured:</span> {emailDiagnostics.smtp_configured ? <span className="text-emerald-600">yes</span> : <span className="text-rose-600">no</span>}</div>
              </div>
              <div className="rounded-md border p-3 space-y-1">
                <p className="font-medium mb-1">Resolved recipients (what would actually be used):</p>
                <div><span className="text-muted-foreground">To:</span> {emailDiagnostics.resolved_to?.length ? emailDiagnostics.resolved_to.join(', ') : <span className="text-rose-600">empty</span>}</div>
                <div><span className="text-muted-foreground">CC:</span> {emailDiagnostics.resolved_cc?.length ? emailDiagnostics.resolved_cc.join(', ') : '—'}</div>
              </div>
              <div className="rounded-md border p-3 space-y-1 text-xs">
                <p className="font-medium text-sm mb-1">Source layers</p>
                <div><span className="text-muted-foreground">Global enabled:</span> {String(emailDiagnostics.tenant_global_enabled)} — to: {emailDiagnostics.tenant_global_to?.join(', ') || '—'} | cc: {emailDiagnostics.tenant_global_cc?.join(', ') || '—'}</div>
                <div><span className="text-muted-foreground">Warehouse override:</span> to: {emailDiagnostics.warehouse_override_to?.join(', ') || '—'} | cc: {emailDiagnostics.warehouse_override_cc?.join(', ') || '—'}</div>
                <div><span className="text-muted-foreground">Tenant warehouse default:</span> to: {emailDiagnostics.tenant_warehouse_default_to?.join(', ') || '—'} | cc: {emailDiagnostics.tenant_warehouse_default_cc?.join(', ') || '—'}</div>
                <div><span className="text-muted-foreground">Notification rule:</span> {emailDiagnostics.rule_enabled ? 'enabled' : 'disabled'} — to: {emailDiagnostics.rule_to?.join(', ') || '—'} | cc: {emailDiagnostics.rule_cc?.join(', ') || '—'}</div>
                {emailDiagnostics.rule_recipients_added_to_warehouse && (
                  <div className="text-muted-foreground" data-testid="po-email-diagnostics-rule-additive">Company rule recipients are added to the warehouse list.</div>
                )}
              </div>
              <div className={`rounded-md border p-3 ${emailDiagnostics.would_attempt ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20' : 'border-amber-200 bg-amber-50 dark:bg-amber-900/20'}`}>
                {emailDiagnostics.would_attempt ? (
                  <p className="text-emerald-900 dark:text-emerald-100"><span className="font-medium">Live send would proceed.</span> Click "Resend Fully-Received Email" — if it still fails, the toast will show the SMTP error from the mail server.</p>
                ) : (
                  <p className="text-amber-900 dark:text-amber-100"><span className="font-medium">Live send would skip.</span> Reason: <span className="font-mono">{emailDiagnostics.would_skip_reason}</span></p>
                )}
              </div>
              <div className="text-xs text-muted-foreground" data-testid="po-email-diagnostics-mode">
                Notification mode: <span className="font-medium text-foreground">{emailDiagnostics.notification_mode === 'every_receipt' ? 'Every receipt' : 'Only when fully received'}</span>
              </div>
              <div className="max-h-72 space-y-1 overflow-y-auto" data-testid="po-email-diagnostics-history">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Notification history</div>
                {emailDiagnostics.history?.length ? emailDiagnostics.history.map((h) => (
                  <NotificationHistoryRow key={h.id} entry={h} onRetry={handleRetryNotification} busy={!!retryingNotificationId} />
                )) : <div className="text-xs text-muted-foreground">No notifications recorded yet.</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDiagnostics(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warranty Credit Prompt Dialog */}
      <AlertDialog open={warrantyCreditPromptOpen} onOpenChange={setWarrantyCreditPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Warranty Credit Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are marking warranty PO <span className="font-semibold">#{editingPO?.po_number}</span> as Received.
              <br /><br />
              Did you receive the warranty credit from the manufacturer?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => {
              setWarrantyCreditPromptOpen(false);
              setPendingWarrantyUpdate(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleWarrantyCreditResponse(false)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="warranty-credit-no-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              No, credit pending
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleWarrantyCreditResponse(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="warranty-credit-yes-btn"
            >
              Yes, credit received
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Printable PO Modal */}
      {printingPO && (
        <PrintablePO
          po={printingPO}
          open={printModalOpen}
          onOpenChange={(open) => {
            setPrintModalOpen(open);
            if (!open) setPrintingPO(null);
          }}
          onEmailClick={() => {
            // Close print modal and open email modal
            setPrintModalOpen(false);
            setEmailingPO(printingPO);
            setEmailModalOpen(true);
          }}
        />
      )}

      {/* Email Vendor Modal */}
      {emailingPO && (
        <EmailVendorModal
          po={emailingPO}
          open={emailModalOpen}
          onOpenChange={(open) => {
            setEmailModalOpen(open);
            if (!open) setEmailingPO(null);
          }}
          onEmailSent={(data) => {
            // Refresh PO list to show updated email audit info
            fetchData();
          }}
        />
      )}

      {/* Partial Receive Modal */}
      <PartialReceiveModal
        open={partialReceiveModalOpen}
        onOpenChange={setPartialReceiveModalOpen}
        purchaseOrder={partialReceivePO}
        onSuccess={handlePartialReceiveSuccess}
      />

      {/* PHASE 3: PO Assignment Modal (triggered when PO is fully received) */}
      <POAssignmentModal
        open={assignmentModalOpen}
        poId={assignmentPO?.id}
        poNumber={assignmentPO?.po_number}
        mode={assignmentMode}
        onSuccess={(result) => {
          closeAssignment(true);
          // Close parent Edit drawer if it's open
          setEditModalOpen(false);
          setEditingPO(null);
        }}
        onOpenChange={(open) => {
          if (!open) closeAssignment();
        }}
      />

      {/* PO Activity Log Modal */}
      <POActivityModal
        poId={activityPO?.id}
        poNumber={activityPO?.po_number}
        open={!!activityPO}
        onClose={() => setActivityPO(null)}
      />

      {/* Phase 8: PO documents (anyone who can open the PO) */}
      <AppDrawer
        open={!!documentsPO}
        onOpenChange={(o) => !o && setDocumentsPO(null)}
        title={`Documents · PO ${documentsPO?.po_number || ''}`}
        description="Quotes, invoices, packing slips and receipts for this purchase order"
        size="md"
        data-testid="po-documents-drawer"
      >
        {documentsPO?.id && <PODocumentsPanel poId={documentsPO.id} source="detail" bordered={false} />}
      </AppDrawer>

      {/* ⚡ Receive all & assign — Confirmation AlertDialog */}
      <AlertDialog open={quickReceiveConfirmOpen} onOpenChange={(open) => {
        if (!open) { setQuickReceiveConfirmOpen(false); setQuickReceivePO(null); }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Receive All Remaining Items?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will receive all remaining items{quickReceivePO ? ` on PO #${quickReceivePO.po_number}` : ''} and immediately open assignment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quick-receive-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => quickReceivePO && executeReceiveAll(quickReceivePO)}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="quick-receive-confirm-action"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile FAB for New PO */}
      <MobileFABContainer data-testid="po-fab-container" className={(assignmentModalOpen || documentsPO || partialReceiveModalOpen || quickReceiveOpen || editModalOpen || newPODrawerOpen) ? 'hidden' : undefined}>
        <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
          <MobileFABMenuItem
            icon={Plus}
            onClick={() => { setFabOpen(false); handleDrawerOpenChange(true); }}
          >
            New PO
          </MobileFABMenuItem>
        </MobileFABMenu>
        <button
          className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
          onClick={() => setFabOpen(!fabOpen)}
          data-testid="po-mobile-fab"
        >
          <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
        </button>
      </MobileFABContainer>
    </Layout>
  );
};
